import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { SUPPORTED_LANGUAGES } from '../lib/constants.js'

/**
 * Initial UI language, resolved before first render so routes outside the
 * `/:lang/` tree (login, reset-password) render in the right language without a
 * post-mount `changeLanguage` (which is fragile on first mount). LanguageRouter
 * still overrides this from the URL on `/:lang/` routes.
 * Order: persisted choice → browser language → 'en'.
 */
function resolveInitialLanguage(): string {
  const supported = SUPPORTED_LANGUAGES as readonly string[]
  try {
    const stored = localStorage.getItem('acp-lang')
    if (stored && supported.includes(stored)) return stored
  } catch { /* storage unavailable */ }
  const nav = typeof navigator !== 'undefined' ? navigator.language?.split('-')[0] : undefined
  return nav && supported.includes(nav) ? nav : 'en'
}

// ── EN (source language) ─────────────────────────────────────────────
import enCommon from './locales/en/common.json'
import enHome from './locales/en/home.json'
import enTrack from './locales/en/track.json'
import enModule from './locales/en/module.json'
import enAuth from './locales/en/auth.json'
import enMastery from './locales/en/mastery.json'
import enConceptMap from './locales/en/conceptMap.json'
import enEngagement from './locales/en/engagement.json'
import enLesson from './locales/en/lesson.json'
import enCourse from './locales/en/course.json'
import enProfile from './locales/en/profile.json'
import enVerify from './locales/en/verify.json'
import enLicense from './locales/en/license.json'
import enInvitation from './locales/en/invitation.json'
import enOrg from './locales/en/org.json'

// ── ES ───────────────────────────────────────────────────────────────
import esCommon from './locales/es/common.json'
import esHome from './locales/es/home.json'
import esTrack from './locales/es/track.json'
import esModule from './locales/es/module.json'
import esAuth from './locales/es/auth.json'
import esMastery from './locales/es/mastery.json'
import esConceptMap from './locales/es/conceptMap.json'
import esEngagement from './locales/es/engagement.json'
import esLesson from './locales/es/lesson.json'
import esCourse from './locales/es/course.json'
import esProfile from './locales/es/profile.json'
import esVerify from './locales/es/verify.json'
import esLicense from './locales/es/license.json'
import esInvitation from './locales/es/invitation.json'
import esOrg from './locales/es/org.json'

const ns = (c: any, h: any, t: any, m: any, a: any, ma: any, cm: any, e: any, l: any, co: any, p: any, v: any, li: any, inv: any, org: any) =>
  ({ common: c, home: h, track: t, module: m, auth: a, mastery: ma, conceptMap: cm, engagement: e, lesson: l, course: co, profile: p, verify: v, license: li, invitation: inv, org })

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: ns(enCommon, enHome, enTrack, enModule, enAuth, enMastery, enConceptMap, enEngagement, enLesson, enCourse, enProfile, enVerify, enLicense, enInvitation, enOrg),
      es: ns(esCommon, esHome, esTrack, esModule, esAuth, esMastery, esConceptMap, esEngagement, esLesson, esCourse, esProfile, esVerify, esLicense, esInvitation, esOrg),
    },
    ns: ['common', 'home', 'track', 'module', 'auth', 'mastery', 'conceptMap', 'engagement', 'lesson', 'course', 'profile', 'verify', 'license', 'invitation', 'org'],
    lng: resolveInitialLanguage(),
    fallbackLng: 'en',
    defaultNS: 'common',
    interpolation: { escapeValue: false },
  })

export default i18n
